import api from '@disco/api/server'
import {
  WATERMARKING_LEAKS_SEARCH_PARAMS_UPDATE,
  WATERMARKING_LOAD,
  WATERMARKING_LOAD_FAIL,
  WATERMARKING_LOAD_NEXT,
  WATERMARKING_LOAD_NEXT_FAIL,
  WATERMARKING_LOAD_NEXT_SUCCESS,
  WATERMARKING_LOAD_SUCCESS,
  WATERMARKING_PLAYLISTS_SEARCH_PARAMS_UPDATE,
} from '@disco/constants/ActionTypes'
import { PLAYLIST, TRACK } from '@disco/constants/ItemTypes'
import type { WatermarkFilters } from '@disco/constants/WatermarkStatusesTypes'
/**
 * Loads watermarking data for tracks or playlists based on query and filters.
 * @param query - Search query string
 * @param filters - Watermark filters for refining search results
 * @param type - Type of item to load (TRACK or PLAYLIST)
 * @returns Thunk that loads watermarking data with leak traces or search results
 */
export const loadWatermarking = (
  query: string,
  filters: WatermarkFilters,
  type: typeof TRACK | typeof PLAYLIST,
): ((dispatch: any) => Promise<any>) => {
  return async (dispatch: (...args: Array<any>) => any) => {
    try {
      dispatch({
        type: WATERMARKING_LOAD,
        query,
        filters,
        itemType: type,
      })
      let result

      if (type === TRACK) {
        result = await api.getLeakTraces({
          q: query,
        })
      } else {
        result = await api.getSearchResultsByQuery(undefined, {
          q: query,
          type,
          ...filters,
        })
      }

      dispatch({
        type: WATERMARKING_LOAD_SUCCESS,
        result,
        itemType: type,
      })
    } catch (error) {
      return dispatch({
        type: WATERMARKING_LOAD_FAIL,
        itemType: type,
        error,
      })
    }
  }
}
/**
 * Loads the next page of watermarking data using pagination URL.
 * @param url - Pagination URL for loading next page
 * @param type - Type of item being paginated (TRACK or PLAYLIST)
 * @returns Thunk that loads next page of watermarking data
 */
export const loadNextWatermarking = (
  url: string,
  type: typeof TRACK | typeof PLAYLIST,
): ((dispatch: any) => Promise<any>) => {
  return async (dispatch: (...args: Array<any>) => any) => {
    try {
      dispatch({
        type: WATERMARKING_LOAD_NEXT,
        itemType: type,
      })

      const result = await api.getUrl<any>(
        url,
        null,
        'api.getLoadNextWatermarking',
      )
      dispatch({
        type: WATERMARKING_LOAD_NEXT_SUCCESS,
        result,
        itemType: type,
      })
    } catch (error) {
      return dispatch({
        type: WATERMARKING_LOAD_NEXT_FAIL,
        itemType: type,
        error,
      })
    }
  }
}
/**
 * Updates search parameters for watermarking playlists view.
 * @param params - Search parameters object
 * @param params.searchTerm - Optional search term to filter playlists
 * @param params.filters - Optional watermark filters for refining results
 * @returns Action object to update watermarking playlists search parameters
 */
export const updateWatermarkingPlaylistsSearchParams = (params: {
  searchTerm?: string
  filters?: WatermarkFilters
}): {
  params: {
    filters?: WatermarkFilters
    searchTerm?: string
  }
  type: string
} => ({
  type: WATERMARKING_PLAYLISTS_SEARCH_PARAMS_UPDATE,
  params,
})
/**
 * Updates search parameters for watermarking leaks view.
 * @param params - Search parameters object
 * @param params.searchTerm - Search term to filter leak traces
 * @returns Action object to update watermarking leaks search parameters
 */
export const updateWatermarkingLeaksSearchParams = (params: {
  searchTerm: string
}): {
  params: {
    searchTerm: string
  }
  type: string
} => ({
  type: WATERMARKING_LEAKS_SEARCH_PARAMS_UPDATE,
  params,
})
